import React from 'react';

/**
 * A compact visual indicator showing all 5 salon doors, with the suggested
 * entrance highlighted (green circle + arrow). Use wherever wayfinding
 * directions are displayed.
 *
 * Usage:
 *   <EntranceDirectionIndicator suggestedEntrance="Salon B" />
 *   <EntranceDirectionIndicator suggestedEntrance="Main Entrance" size="sm" />
 */

const DOORS = [
  { key: 'salon_a', label: 'A' },
  { key: 'salon_b', label: 'B' },
  { key: 'main', label: 'M' },
  { key: 'salon_d', label: 'D' },
  { key: 'salon_e', label: 'E' },
] as const;

/** Map various string representations to our key */
function matchDoor(suggested: string): string | null {
  if (!suggested) return null;
  const lower = suggested.toLowerCase().trim();
  if (lower.includes('salon a') || lower === 'a') return 'salon_a';
  if (lower.includes('salon b') || lower === 'b') return 'salon_b';
  if (lower.includes('main') || lower.includes('center') || lower === 'm') return 'main';
  if (lower.includes('salon d') || lower === 'd') return 'salon_d';
  if (lower.includes('salon e') || lower === 'e') return 'salon_e';
  return null;
}

interface EntranceDirectionIndicatorProps {
  /** The suggested entrance name, e.g. "Salon B" or "Main Entrance" */
  suggestedEntrance: string | null | undefined;
  /** Size variant: 'sm' (inline) or 'md' (card). Default 'sm' */
  size?: 'sm' | 'md';
}

export function EntranceDirectionIndicator({ suggestedEntrance, size = 'sm' }: EntranceDirectionIndicatorProps) {
  const highlightKey = matchDoor(suggestedEntrance || '');
  const isSm = size === 'sm';
  const doorSize = isSm ? 22 : 32;
  const fontSize = isSm ? '0.55rem' : '0.7rem';
  const gap = isSm ? '6px' : '10px';
  const arrowSize = isSm ? '0.6rem' : '0.8rem';

  return (
    <div style={{ display: 'inline-flex', alignItems: 'flex-end', gap, verticalAlign: 'middle' }} aria-label={`Suggested entrance: ${suggestedEntrance || 'none'}`}>
      {DOORS.map((door) => {
        const isHighlighted = door.key === highlightKey;
        return (
          <div key={door.key} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1px' }}>
            {/* Arrow indicator above highlighted door */}
            {isHighlighted && (
              <div style={{ fontSize: arrowSize, lineHeight: 1, color: '#1a6b3c' }}>▼</div>
            )}
            {/* Door circle */}
            <div
              style={{
                width: `${doorSize}px`,
                height: `${doorSize}px`,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize,
                fontWeight: 700,
                color: isHighlighted ? '#fff' : '#666',
                backgroundColor: isHighlighted ? '#1a6b3c' : '#e5e7eb',
                border: isHighlighted ? '2px solid #166534' : '1px solid #d1d5db',
                transition: 'all 0.2s',
                boxShadow: isHighlighted ? '0 2px 6px rgba(26,107,60,0.4)' : 'none',
              }}
              title={door.key === 'main' ? 'Main Entrance' : `Salon ${door.label}`}
            >
              {door.label}
            </div>
          </div>
        );
      })}
    </div>
  );
}
